import { Big } from "big.js";
import { ZERO } from "./bigUtil.ts";
import { Cost, genericOf, Stage } from "./CaseConfiguration.ts";

/**
 * What a cost of a party is incurred towards: its part towards each opponent
 * and its generic part, owed by every opponent the party wins against.
 */
export function sharesOf(cost: Cost): {
  towards: { [opponentId: string]: Big };
  generic: Big;
} {
  const towards: { [opponentId: string]: Big } = {};
  for (const [opponentId, part] of Object.entries(cost.towards ?? {}))
    if (part > 0) towards[opponentId] = new Big(part);
  return { towards, generic: genericOf(cost) };
}

/** An accepted settlement in which a party receives a sum, and what it apportioned to costs. */
export type SettlementUse = {
  readonly offerId: string;
  readonly opponentId: string;
  readonly stage: Stage;
  /** Its place in the case's offers, which orders settlements of one stage. */
  readonly order: number;
  readonly ofSettlement: number;
  readonly towardsPayer: number;
  readonly generic: number;
};

/**
 * What a party's spent costs offer the settlements in which it receives a
 * sum, and what its accepted settlements have used up of them. Costs count
 * as the party pays them, with the VAT it cannot reclaim: the costs a costs
 * order recovers.
 */
export type SettlementBasis = {
  /** Its spent costs of the Costs page, by the opponent they are incurred towards. */
  readonly towards: { readonly [opponentId: string]: number };
  /** Its spent costs of the Costs page that are generic. */
  readonly generic: number;
  /** Its settlement costs of each offer, towards the offer's counter-party. */
  readonly offerCosts: ReadonlyArray<{
    readonly offerId: string;
    readonly opponentId: string;
    readonly stage: Stage;
    readonly amount: number;
  }>;
  /** Its accepted settlements, each with what it apportioned to costs. */
  readonly used: ReadonlyArray<SettlementUse>;
};

/** The settlement basis of a party with nothing in it. */
export const NO_SETTLEMENT_BASIS: SettlementBasis = {
  towards: {},
  generic: 0,
  offerCosts: [],
  used: [],
};

/** Whether a settlement is reached before another: by stage, then as the offers are listed. */
export const isBefore = (
  a: { stage: Stage; order: number },
  b: { stage: Stage; order: number },
): boolean => a.stage < b.stage || (a.stage === b.stage && a.order < b.order);

/** A settlement in which a party receives a sum from `opponentId`. */
export type SettlementPlace = {
  readonly offerId: string;
  readonly opponentId: string;
  readonly stage: Stage;
  readonly order: number;
};

/**
 * The caps of the costs rows of a settlement in which a party receives a
 * sum: what its costs offer, less what the settlements it reached before
 * have used up of them. Nothing is covered twice.
 *
 * - "Settlement costs": its settlement costs towards the opponent, of every
 *   offer between the two at the settlement's stage or before. The offer's
 *   own are `ownCosts` where given, as while they are being entered.
 * - "Costs towards …": its spent costs of the Costs page towards the opponent.
 * - "Remaining costs": its spent generic costs of the Costs page; costs
 *   towards another opponent are that opponent's.
 */
export function settlementCaps(
  basis: SettlementBasis,
  place: SettlementPlace,
  ownCosts?: number,
): { ofSettlement: number; towardsPayer: number; generic: number } {
  const { offerId, opponentId, stage } = place;
  let ofSettlement = ZERO;
  for (const offer of basis.offerCosts) {
    if (offer.opponentId !== opponentId || offer.stage > stage) continue;
    if (offer.offerId === offerId && ownCosts !== undefined) continue;
    ofSettlement = ofSettlement.add(offer.amount);
  }
  if (ownCosts !== undefined) ofSettlement = ofSettlement.add(ownCosts);
  let towardsPayer = new Big(basis.towards[opponentId] ?? 0);
  let generic = new Big(basis.generic);
  for (const use of basis.used) {
    if (use.offerId === offerId || !isBefore(use, place)) continue;
    generic = generic.minus(use.generic);
    if (use.opponentId !== opponentId) continue;
    ofSettlement = ofSettlement.minus(use.ofSettlement);
    towardsPayer = towardsPayer.minus(use.towardsPayer);
  }
  const atLeastZero = (amount: Big) => Math.max(0, amount.toNumber());
  return {
    ofSettlement: atLeastZero(ofSettlement),
    towardsPayer: atLeastZero(towardsPayer),
    generic: atLeastZero(generic),
  };
}
